/**
 * ICP Status Calculator Utility
 *
 * Determines whether ICP test parameters are within acceptable ranges
 * and provides aggregated status information.
 */

import { parseRange } from './rangeParser';
import type { ICPTestRecord, ReferenceRange } from '@/types/sensor';

export type ParameterStatus = 'in-range' | 'out-of-range' | 'no-range';
export type ParameterStatusDetailed = 'in-range' | 'above-range' | 'below-range' | 'no-range';

/**
 * Calculate the status of a parameter value against its reference range.
 *
 * @param value - The measured parameter value
 * @param rangeStr - The reference range string (e.g., "6.2 - 8.5 dKH")
 * @returns Status indicating if value is in-range, out-of-range, or no-range available
 *
 * @example
 * calculateStatus(7.5, "6.2 - 8.5 dKH") // 'in-range'
 * calculateStatus(9.0, "6.2 - 8.5 dKH") // 'out-of-range'
 * calculateStatus(7.5, undefined) // 'no-range'
 */
export function calculateStatus(
  value: number,
  rangeStr?: string
): ParameterStatus {
  if (!rangeStr) return 'no-range';

  const parsed = parseRange(rangeStr);
  if (!parsed) return 'no-range';

  return value >= parsed.min && value <= parsed.max
    ? 'in-range'
    : 'out-of-range';
}

/**
 * Calculate detailed status with direction information for out-of-range values.
 *
 * @param value - The measured parameter value
 * @param rangeStr - The reference range string (e.g., "6.2 - 8.5 dKH")
 * @returns Detailed status indicating if value is in-range, above-range, below-range, or no-range
 *
 * @example
 * calculateStatusDetailed(7.5, "6.2 - 8.5 dKH") // 'in-range'
 * calculateStatusDetailed(9.0, "6.2 - 8.5 dKH") // 'above-range'
 * calculateStatusDetailed(5.0, "6.2 - 8.5 dKH") // 'below-range'
 * calculateStatusDetailed(7.5, undefined) // 'no-range'
 */
export function calculateStatusDetailed(
  value: number,
  rangeStr?: string
): ParameterStatusDetailed {
  if (!rangeStr) return 'no-range';

  const parsed = parseRange(rangeStr);
  if (!parsed) return 'no-range';

  if (value >= parsed.min && value <= parsed.max) {
    return 'in-range';
  } else if (value > parsed.max) {
    return 'above-range';
  } else {
    return 'below-range';
  }
}

/**
 * Resolve a parameter's status, preferring the provider's own verdict.
 *
 * Some providers (ATI) report a status per parameter instead of a numeric
 * set-point range. When `providerStatus` is present and recognized it wins;
 * otherwise we fall back to our own range arithmetic via
 * `calculateStatusDetailed`.
 *
 * @param providerStatus - Provider-reported status (ATI: "NORMAL" |
 *   "ABOVE NORMAL" | "BELOW NORMAL"); null/undefined/unrecognized falls back
 * @param value - The measured value (null for below-detection readings)
 * @param setPointRange - The set-point range string used by the fallback
 * @returns Detailed status with direction retained for out-of-range arrows
 *
 * @example
 * resolveStatus('NORMAL', 99, '6.2 - 8.5 dKH')   // 'in-range' (provider wins)
 * resolveStatus('ABOVE NORMAL', 9.0, undefined)  // 'above-range'
 * resolveStatus(null, 9.0, '6.2 - 8.5 dKH')      // 'above-range' (fallback)
 * resolveStatus(null, null, '6.2 - 8.5 dKH')     // 'no-range'
 */
export function resolveStatus(
  providerStatus: string | null | undefined,
  value: number | null | undefined,
  setPointRange?: string | null
): ParameterStatusDetailed {
  switch (providerStatus?.trim().toUpperCase()) {
    case 'NORMAL':
      return 'in-range';
    case 'ABOVE NORMAL':
      return 'above-range';
    case 'BELOW NORMAL':
      return 'below-range';
  }

  if (typeof value !== 'number') return 'no-range';
  return calculateStatusDetailed(value, setPointRange ?? undefined);
}

/**
 * Collapse a detailed (directional) status into the simple three-state form.
 */
export function toSimpleStatus(detailed: ParameterStatusDetailed): ParameterStatus {
  if (detailed === 'above-range' || detailed === 'below-range') {
    return 'out-of-range';
  }
  return detailed;
}

/**
 * Count how many parameters in an ICP test are outside their acceptable ranges.
 *
 * Uses the same provider-aware resolution as row rendering and the
 * out-of-range filter (`resolveStatus` + `toSimpleStatus`): a provider
 * verdict (`provider_status`, e.g. ATI's "ABOVE NORMAL") wins; otherwise we
 * fall back to `set_point_range` arithmetic on the numeric value.
 *
 * @param test - The ICP test record with parameter values
 * @param ranges - Array of reference ranges for parameters
 * @param getValue - Optional resolver mapping a range row to its measured
 *   value. Pass this when `range.parameter` is not a direct key of the test
 *   record (e.g. ranges keyed by ICP symbol like 'Alk' while values live in
 *   DB columns like 'alkalinity'). Defaults to `test[range.parameter]`.
 * @returns Count of parameters that are out of range
 *
 * @example
 * const test = { alkalinity: 9.5, calcium: 420, magnesium: 1350 };
 * const ranges = [
 *   { parameter: 'alkalinity', set_point_range: '7.0 - 9.0 dKH' },
 *   { parameter: 'calcium', set_point_range: '380 - 450 ppm' },
 *   { parameter: 'magnesium', set_point_range: '1250 - 1400 ppm' }
 * ];
 * countOutOfRange(test, ranges) // 1 (alkalinity is out of range)
 */
export function countOutOfRange(
  test: ICPTestRecord,
  ranges: ReferenceRange[],
  getValue?: (range: ReferenceRange) => number | null | undefined
): number {
  let count = 0;

  ranges.forEach((range) => {
    const raw = getValue
      ? getValue(range)
      : test[range.parameter as keyof ICPTestRecord];
    const value = typeof raw === 'number' ? raw : null;

    const detailed = resolveStatus(
      range.provider_status,
      value,
      range.set_point_range
    );
    if (toSimpleStatus(detailed) === 'out-of-range') {
      count++;
    }
  });

  return count;
}

/**
 * Get the status color for visual indicators.
 *
 * @param status - The parameter status
 * @returns Mantine color name
 */
export function getStatusColor(status: ParameterStatus): string {
  switch (status) {
    case 'in-range':
      return 'green';
    case 'out-of-range':
      return 'red';
    case 'no-range':
      return 'gray';
  }
}
